import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Stethoscope, Building, User, MessageSquarePlus, Clock, Inbox, Loader2 } from 'lucide-react';

const STAGE_TONE = {
  'Patient Query': 'bg-amber-100 text-amber-700',
  'Under Review': 'bg-blue-100 text-blue-700',
  'Assigned': 'bg-purple-100 text-purple-700',
  'In Progress': 'bg-indigo-100 text-indigo-700',
  'Completed': 'bg-green-100 text-green-700',
};

const sourceIcon = (source) => {
  if ((source || '').includes('doctor')) return User;
  if ((source || '').includes('hospital')) return Building;
  return Stethoscope;
};

const sourceLabel = (source) => {
  if ((source || '').includes('doctor')) return 'Doctor consultation';
  if ((source || '').includes('hospital')) return 'Hospital consultation';
  if ((source || '').includes('treatment')) return 'Treatment enquiry';
  return 'Website enquiry';
};

export default function MyRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/patient/my-requests');
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error('Could not load your requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/patient/my-requests/${noteFor.inquiry_id}/note`, { note: noteText.trim() });
      toast.success('Note sent to your care team');
      setNoteFor(null);
      setNoteText('');
      load();
    } catch (e) {
      toast.error('Could not send your note');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Loading your requests…</div>;
  }

  return (
    <div className="space-y-6" data-testid="my-requests-section">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Requests</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Track every consultation you've requested and its current status.</p>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 dark:text-slate-300 font-medium">No requests yet</p>
            <p className="text-slate-400 text-sm mt-1">Browse treatments, doctors or hospitals and hit "Request a Consultation" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => {
            const Icon = sourceIcon(r.source);
            return (
              <Card key={r.inquiry_id} className="overflow-hidden" data-testid="my-request-card">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">{r.treatment_name || 'Consultation'}</p>
                          <p className="text-xs text-slate-500">{sourceLabel(r.source)}</p>
                        </div>
                        <Badge className={`${STAGE_TONE[r.stage_name] || 'bg-slate-100 text-slate-700'} border-0`} data-testid="my-request-status">
                          {r.stage_name}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</span>
                        {r.case_manager_name && <span>Coordinator: {r.case_manager_name}</span>}
                      </div>

                      {r.notes && r.notes.length > 0 && (
                        <div className="mt-3 space-y-1.5 border-l-2 border-slate-100 dark:border-slate-800 pl-3">
                          {r.notes.map((n, i) => (
                            <div key={i} className="text-sm text-slate-600 dark:text-slate-300">
                              <span className="text-slate-400 text-xs mr-2">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</span>
                              {n.note}
                            </div>
                          ))}
                        </div>
                      )}

                      <Button
                        variant="outline" size="sm" className="mt-3"
                        onClick={() => { setNoteFor(r); setNoteText(''); }}
                        data-testid="add-note-btn"
                      >
                        <MessageSquarePlus className="w-4 h-4 mr-2" /> Add follow-up note
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle>Add a follow-up note</DialogTitle>
            <DialogDescription>
              {noteFor?.treatment_name} — your care team will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Share an update, a question, or new information..."
            rows={4}
            data-testid="note-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteFor(null)}>Cancel</Button>
            <Button onClick={submitNote} disabled={saving || !noteText.trim()} data-testid="note-submit-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
